// Downscale + re-encode a photo as jpeg so it stays well under the server's upload limit (2048 KB per field).
// Keeps the aspect ratio, never upscales, and lowers the quality step by step when the result is still too big.
// A file that is not an image is returned as it is.
const MIN_QUALITY = 0.4;
const QUALITY_STEP = 0.1;

const loadImage = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = (err) => {
      URL.revokeObjectURL(url);
      reject(err);
    };
    img.src = url;
  });

const canvasToBlob = (canvas, quality) =>
  new Promise((resolve, reject) =>
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("toBlob failed"))),
      "image/jpeg",
      quality
    )
  );

const compressImage = async (
  file,
  { maxW = 1600, maxH = 1600, quality = 0.75, maxSizeKB = 1024 } = {}
) => {
  if (!file || !file.type || !file.type.startsWith("image/")) return file;

  const img = await loadImage(file);

  // browsers apply the exif orientation when an <img> is drawn to a canvas
  const ratio = Math.min(maxW / img.width, maxH / img.height, 1);
  const targetW = Math.round(img.width * ratio);
  const targetH = Math.round(img.height * ratio);

  const canvas = document.createElement("canvas");
  canvas.width = targetW;
  canvas.height = targetH;
  canvas.getContext("2d").drawImage(img, 0, 0, targetW, targetH);

  let currentQuality = quality;
  let blob = await canvasToBlob(canvas, currentQuality);
  while (blob.size > maxSizeKB * 1024 && currentQuality > MIN_QUALITY) {
    currentQuality = Math.max(MIN_QUALITY, currentQuality - QUALITY_STEP);
    blob = await canvasToBlob(canvas, currentQuality);
  }

  return new File([blob], `photo_${Date.now()}.jpg`, { type: "image/jpeg" });
};

export default compressImage;
